import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import RiskyAnalysisCard from '../components/RiskyAnalysisCard';
import { formatMatchTime } from '../utils/time';

const FILTERS = {
  highestEdge: 'highestEdge',
  highestModel: 'highestModel',
  controlledRisk: 'controlledRisk',
  firstHalf: 'firstHalf',
  score: 'score',
  htft: 'htft',
  cornersCards: 'cornersCards'
};

const MAX_RISKY_MATCHES = 40;

const getRiskOrder = (riskLevel = '') => {
  if (riskLevel === 'Kontrollu Risk') return 3;
  if (riskLevel === 'Orta Risk') return 2;
  if (riskLevel === 'Yuksek Risk') return 1;
  return 0;
};

const matchesFilter = (pick, filter) => {
  if (filter === FILTERS.controlledRisk) return pick.riskLevel === 'Kontrollu Risk';
  if (filter === FILTERS.firstHalf) return pick.market?.group === 'ilk-yari';
  if (filter === FILTERS.score) return pick.market?.group === 'skor';
  if (filter === FILTERS.htft) return pick.market?.group === 'ht-ft';
  if (filter === FILTERS.cornersCards) return ['korner-kart', 'korner', 'kart'].includes(pick.market?.group);
  return true;
};

const sortPicks = (picks, filter) => {
  const sorted = [...picks];

  if (filter === FILTERS.highestModel) {
    return sorted.sort((a, b) => Number(b.modelProbability || 0) - Number(a.modelProbability || 0));
  }

  if (filter === FILTERS.controlledRisk) {
    return sorted.sort((a, b) => {
      const riskDiff = getRiskOrder(b.riskLevel) - getRiskOrder(a.riskLevel);
      if (riskDiff !== 0) return riskDiff;
      return Number(b.riskyScore || 0) - Number(a.riskyScore || 0);
    });
  }

  return sorted.sort((a, b) => {
    const edgeDiff = Number(b.edge || 0) - Number(a.edge || 0);
    if (edgeDiff !== 0) return edgeDiff;
    const scoreDiff = Number(b.riskyScore || 0) - Number(a.riskyScore || 0);
    if (scoreDiff !== 0) return scoreDiff;
    return Number(b.confidence || 0) - Number(a.confidence || 0);
  });
};

const RiskyAnalysis = () => {
  const [analyses, setAnalyses] = useState([]);
  const [summary, setSummary] = useState({ total: 0, marketGroupCounts: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [activeFilter, setActiveFilter] = useState(FILTERS.highestEdge);

  useEffect(() => {
    const fetchRisky = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await api.getRiskyPicks();
        setAnalyses(data.analyses || []);
        setSummary(data.summary || { total: 0, marketGroupCounts: {} });
        setRefreshing(data?.status === 'preparing');
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRisky();
  }, []);

  const filteredAnalyses = useMemo(() => {
    const filtered = analyses.filter((pick) => matchesFilter(pick, activeFilter));
    return sortPicks(filtered, activeFilter).slice(0, MAX_RISKY_MATCHES);
  }, [analyses, activeFilter]);

  if (loading || refreshing) return <div className="loading">Bugunun analizleri hazirlaniyor. Lutfen birkac dakika sonra tekrar kontrol edin.</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="page">
      <section className="hero-panel compact">
        <div className="hero-copy">
          <span className="hero-badge">Riskli Premium Oda</span>
          <h1 className="live-hero-title">VERIYLE DESTEKLENEN RISKLI MARKETLER.</h1>
          <p>
            Bu bolum yuksek oran potansiyeli tasiyan riskli marketleri listeler. Oranlar gosterilmez;
            model yuzdesi, edge ve veri sinyali uzerinden degerlendirme yapilir.
          </p>
        </div>
      </section>

      <section className="content-panel">
        <div className="section-heading">
          <div>
            <h2>Riskli Denenebilir</h2>
            <p>
              Bugun {summary.total || filteredAnalyses.length} riskli premium secim bulundu. Sadece veriyle
              desteklenen agresif marketler listelenir.
            </p>
          </div>
          <div className="filter-group">
            <button
              className={`filter-chip ${activeFilter === FILTERS.highestEdge ? 'active' : ''}`}
              onClick={() => setActiveFilter(FILTERS.highestEdge)}
            >
              En Yuksek Edge
            </button>
            <button
              className={`filter-chip ${activeFilter === FILTERS.highestModel ? 'active' : ''}`}
              onClick={() => setActiveFilter(FILTERS.highestModel)}
            >
              En Yuksek Model %
            </button>
            <button
              className={`filter-chip ${activeFilter === FILTERS.controlledRisk ? 'active' : ''}`}
              onClick={() => setActiveFilter(FILTERS.controlledRisk)}
            >
              Kontrollu Risk
            </button>
            <button
              className={`filter-chip ${activeFilter === FILTERS.firstHalf ? 'active' : ''}`}
              onClick={() => setActiveFilter(FILTERS.firstHalf)}
            >
              Ilk Yari
            </button>
            <button
              className={`filter-chip ${activeFilter === FILTERS.score ? 'active' : ''}`}
              onClick={() => setActiveFilter(FILTERS.score)}
            >
              Skor
            </button>
            <button
              className={`filter-chip ${activeFilter === FILTERS.htft ? 'active' : ''}`}
              onClick={() => setActiveFilter(FILTERS.htft)}
            >
              HT/FT
            </button>
            <button
              className={`filter-chip ${activeFilter === FILTERS.cornersCards ? 'active' : ''}`}
              onClick={() => setActiveFilter(FILTERS.cornersCards)}
            >
              Korner/Kart
            </button>
          </div>
        </div>
      </section>

      {filteredAnalyses.length === 0 ? (
        <div className="empty">
          Bugun riskli premium bolumunde veriyle desteklenen secim bulunmuyor.
        </div>
      ) : (
        <div className="analysis-grid">
          {filteredAnalyses.map((analysis) => (
            <RiskyAnalysisCard
              key={analysis._id || `${analysis.matchId}-${analysis.market?.key}`}
              analysis={analysis}
              matchTime={formatMatchTime(analysis.matchDate)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default RiskyAnalysis;
